// Gestiona el renderizador, la cámara, las luces y el bucle de render de Three.js.
// Independiente del contenido: las escenas concretas (GameScene) añaden objetos.
import * as THREE from 'three';
import { clamp } from '../utils/helpers.js';
import boardEmblemUrl from '../assets/board-emblem.webp';

export class SceneManager {
  constructor(canvas, { quality = 'auto', shadows = true } = {}) {
    this.canvas = canvas;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color('#0a0e1a'); // azul noche (estética "Nocturno Neón")

    this.isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    this.quality = quality === 'auto' ? (this.isMobile ? 'low' : 'high') : quality;
    // Vista 2D plana: SIN sombras. Las fichas se ven planas, como las cartas de la mano.
    this.shadowsEnabled = false;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: this.quality !== 'low',
      powerPreference: 'high-performance',
      alpha: false,
    });
    this._applyPixelRatio();
    this.renderer.shadowMap.enabled = this.shadowsEnabled;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    // Cámara ORTOGRÁFICA cenital (vista 2D desde arriba). El tablero se ve plano,
    // igual que las cartas de la mano.
    this.camera = new THREE.OrthographicCamera(-10, 10, 10, -10, 0.1, 200);
    this.camera.up.set(0, 0, -1); // -Z hacia la parte superior de la pantalla
    this.camera.position.set(0, 50, 0);
    this.camera.lookAt(0, 0, 0);

    // El ORIGEN (userZoom=1) es el zoom MÁXIMO: las fichas del tablero tienen el mismo
    // tamaño que las de la mano. Se puede ALEJAR (userZoom<1) para ver más tablero.
    this.PPU = 58;        // píxeles por unidad de mundo en el origen
    this.ZMIN = 0.3;      // zoom mínimo (más alejado)
    // Permite ACERCAR más allá del origen para que el contenido llene el ancho de la
    // pantalla (con pocas palabras, si no quedaría todo pequeño y centrado).
    this.ZMAX = 2.4;
    this.userZoom = 1;    // 1 = origen
    this.HAND_PX = 178;   // altura reservada abajo para la mano
    this.panX = 0;
    this.panZ = 0;
    this.contentBounds = null; // {minX,maxX,minZ,maxZ} del contenido (para limitar el paneo)

    this._setupLights();
    this._setupTable();
    this._setupWatermark();
    this._setupBigTimer();

    this._updaters = new Set();
    this._raf = null;
    this._clock = new THREE.Clock();
    this._onResize = this._onResize.bind(this);
    window.addEventListener('resize', this._onResize);
    window.addEventListener('orientationchange', this._onResize);
    this.resize();
  }

  _applyPixelRatio() {
    const maxPR = this.quality === 'low' ? 1.5 : 2;
    this.renderer.setPixelRatio(clamp(window.devicePixelRatio, 1, maxPR));
  }

  _setupLights() {
    // Iluminación plana y cálida para la vista cenital 2D (bien legible).
    const ambient = new THREE.AmbientLight(0xfff2dd, 0.9);
    this.scene.add(ambient);

    const key = new THREE.DirectionalLight(0xffe6b8, 0.85);
    key.position.set(-3, 18, 5); // casi cenital -> sombra corta bajo las fichas
    key.castShadow = this.shadowsEnabled;
    if (this.shadowsEnabled) {
      key.shadow.mapSize.set(this.quality === 'high' ? 2048 : 1024, this.quality === 'high' ? 2048 : 1024);
      key.shadow.camera.near = 1;
      key.shadow.camera.far = 40;
      const d = 14;
      key.shadow.camera.left = -d;
      key.shadow.camera.right = d;
      key.shadow.camera.top = d;
      key.shadow.camera.bottom = -d;
      key.shadow.bias = -0.0005;
    }
    this.scene.add(key);
    this.keyLight = key;

    const fill = new THREE.DirectionalLight(0xa9c7ff, 0.35);
    fill.position.set(8, 6, -4);
    this.scene.add(fill);
  }

  _setupTable() {
    // Mesa: un ÚNICO plano enorme (tablero "infinito") que REPITE la imagen
    // tablero.webp como una malla. La textura está diseñada para empalmar por
    // cualquier borde: al crecer el tablero simplemente se ven más baldosas,
    // sin deformar la imagen (repeat = tamaño_plano / tamaño_baldosa).
    const PLANE = 600;                 // lado del plano en unidades de mundo
    const geo = new THREE.PlaneGeometry(PLANE, PLANE);
    // Fondo del tablero: azul noche liso (estética "Nocturno Neón"), sin iluminar
    // para que el color sea exacto y las fichas de neón resalten al máximo.
    const mat = new THREE.MeshBasicMaterial({ color: 0x0b1120 });
    const felt = new THREE.Mesh(geo, mat);
    felt.rotation.x = -Math.PI / 2;
    felt.position.y = 0;
    this.scene.add(felt);
    this.table = felt;
  }

  _setupWatermark() {
    // Marca de agua del emblema P (blanco y negro) sobre el tablero, bajo las fichas.
    // Sigue a la cámara para quedar siempre centrada en pantalla.
    const geo = new THREE.PlaneGeometry(1, 1);
    const mat = new THREE.MeshBasicMaterial({
      transparent: true, opacity: 0.05, depthWrite: false, color: 0xffffff,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(0, 0.05, 0);
    mesh.renderOrder = -1; // se dibuja antes que las fichas
    mesh.visible = false;  // se activa al entrar a la partida
    this.scene.add(mesh);
    this.watermark = mesh;
    this._wmAspect = 1;
    const loader = new THREE.TextureLoader();
    loader.load(boardEmblemUrl, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy?.() || 1;
      mat.map = tex; mat.needsUpdate = true;
      const img = tex.image;
      if (img && img.width && img.height) this._wmAspect = img.width / img.height;
      this._applyCamera();
    });
  }

  setWatermarkVisible(v) {
    if (this.watermark) this.watermark.visible = !!v;
  }

  // --------------------------------------------------------------------------
  // Reloj GRANDE central (últimos 10 s del turno): DENTRO de la escena 3D, plano sobre el
  // tablero (renderOrder -1) para quedar POR DETRÁS de las fichas y sobre el fondo, con pulso.
  // --------------------------------------------------------------------------
  _setupBigTimer() {
    const cv = document.createElement('canvas');
    cv.width = 512; cv.height = 512;
    this._btCanvas = cv;
    this._btCtx = cv.getContext('2d');
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    this._btTexture = tex;
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(0, 0.06, 0);
    mesh.renderOrder = -1; // se dibuja ANTES que las fichas -> queda por detrás
    mesh.visible = false;
    this.scene.add(mesh);
    this.bigTimer = mesh;
    this._btBaseScale = 1;
    this._btLast = null;
  }

  _drawBigTimer(secs, ratio) {
    const ctx = this._btCtx; const S = 512; const c = S / 2;
    ctx.clearRect(0, 0, S, S);
    const R = 205; const lw = 26;
    // Pista (aro tenue).
    ctx.beginPath(); ctx.arc(c, c, R, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.16)'; ctx.lineWidth = lw * 0.7; ctx.stroke();
    // Progreso: desde arriba, en sentido horario, longitud = fracción de tiempo restante.
    const start = -Math.PI / 2;
    const end = start + Math.PI * 2 * Math.max(0, Math.min(1, ratio));
    ctx.beginPath(); ctx.arc(c, c, R, start, end);
    ctx.strokeStyle = '#ff5a4d'; ctx.lineWidth = lw; ctx.lineCap = 'round';
    ctx.shadowColor = 'rgba(255,59,48,0.95)'; ctx.shadowBlur = 26; ctx.stroke();
    ctx.shadowBlur = 0;
    // Número grande.
    ctx.fillStyle = '#ff5a4d';
    ctx.font = '900 240px "Trebuchet MS", system-ui, sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = 20; ctx.shadowOffsetY = 8;
    ctx.fillText(String(secs), c, c + 10);
    ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    this._btTexture.needsUpdate = true;
  }

  /** Muestra/actualiza el reloj grande. show=false lo oculta. */
  updateBigTimer(secs, ratio, show) {
    if (!this.bigTimer) return;
    if (!show) { this.bigTimer.visible = false; this._btLast = null; return; }
    const key = `${secs}|${Math.round(ratio * 100)}`;
    if (key !== this._btLast) { this._btLast = key; this._drawBigTimer(secs, ratio); }
    this.bigTimer.visible = true;
  }

  addUpdater(fn) {
    this._updaters.add(fn);
    return () => this._updaters.delete(fn);
  }

  start() {
    if (this._raf) return;
    const loop = () => {
      const dt = this._clock.getDelta();
      // Pulso del reloj grande (late como el pequeño): escala ±7% cada ~1 s.
      if (this.bigTimer && this.bigTimer.visible) {
        this._btTime = (this._btTime || 0) + dt;
        const p = 1 + 0.07 * Math.sin(this._btTime * Math.PI * 2);
        const s = (this._btBaseScale || 1) * p;
        this.bigTimer.scale.set(s, s, 1);
      }
      for (const fn of this._updaters) fn(dt);
      this.renderer.render(this.scene, this.camera);
      this._raf = requestAnimationFrame(loop);
    };
    this._raf = requestAnimationFrame(loop);
  }

  stop() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
  }

  _onResize() {
    this.resize();
  }

  vw() { return window.innerWidth || 1280; }
  vh() { return window.innerHeight || 720; }

  resize() {
    const w = this.vw();
    const h = this.vh();
    this.renderer.setSize(w, h, false);
    this._applyCamera();
    this._applyBoardClip();
  }

  /**
   * Recorta el canvas del tablero a la ZONA ÚTIL en VERTICAL, para que NINGUNA ficha se
   * dibuje sobre las franjas del HUD (jugadores arriba · mano y botones abajo) aunque el
   * jugador haga zoom/paneo. El fondo del body (#0a0e1a) coincide con el del tablero, así
   * que el recorte no deja costura visible. En horizontal no se recorta (no lo pidió).
   */
  _applyBoardClip() {
    const el = this.renderer?.domElement;
    if (!el) return;
    const w = this.vw();
    const h = this.vh();
    if (h <= w) { el.style.clipPath = ''; return; } // solo vertical
    const u = this._usableArea();
    const left = Math.max(0, Math.round(u.offX + w / 2 - u.usableW / 2));
    const top = Math.max(0, Math.round(u.offY + h / 2 - u.usableH / 2));
    const right = Math.max(0, w - left - Math.round(u.usableW));
    const bottom = Math.max(0, h - top - Math.round(u.usableH));
    el.style.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px)`;
  }

  _scale() { return this.PPU * this.userZoom; }

  /** Ajusta el zoom (rueda/pinch). >1 acerca, <1 aleja. Nunca supera el origen (máx 1). */
  adjustZoom(mult) {
    this.userZoom = clamp(this.userZoom * mult, this.ZMIN, this.ZMAX);
    this._clampPan();
    this._applyCamera();
  }

  /** Desplaza la cámara por un delta EN PÍXELES de pantalla. */
  panByPixels(dxPx, dyPx) {
    const s = this._scale();
    this.panX += -dxPx / s;
    this.panZ += -dyPx / s;
    this._clampPan();
    this._applyCamera();
  }

  /**
   * Impide arrastrar el tablero FUERA de la pantalla: acota panX/panZ para que el
   * contenido (this.contentBounds, en coords de mundo) siga solapando la vista.
   */
  _clampPan() {
    const b = this.contentBounds;
    if (!b) return;
    const w = this.vw();
    const h = this.vh();
    const s = this._scale();
    const vw2 = (w / s) / 2;
    const vh2 = (h / s) / 2;
    const axis = (pan, lo0, hi0, v2) => {
      const keep = Math.min(2, (hi0 - lo0) / 2 + 0.01, v2); // cuánto contenido mantener a la vista
      const lo = lo0 - v2 + keep;
      const hi = hi0 + v2 - keep;
      if (lo > hi) return (lo0 + hi0) / 2;
      return clamp(pan, lo, hi);
    };
    this.panX = axis(this.panX, b.minX, b.maxX, vw2);
    this.panZ = axis(this.panZ, b.minZ, b.maxZ, vh2);
  }

  /** Alto real (px) de la safe-area superior (notch / barra de estado). Se mide con un
   *  probe oculto porque env() no es legible directamente desde JS. */
  _safeAreaTop() {
    let p = this._saProbe;
    if (!p) {
      p = document.createElement('div');
      p.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;padding-top:var(--sat,0px);';
      document.body.appendChild(p);
      this._saProbe = p;
    }
    return parseFloat(getComputedStyle(p).paddingTop) || 0;
  }

  /** Zona útil (excluye barra superior, acciones a la derecha y la mano). */
  _usableArea() {
    const w = this.vw();
    const h = this.vh();
    const portrait = h > w; // móvil vertical: info arriba, mano+acciones abajo, laterales mínimos
    const compact = !portrait && h <= 480; // móvil apaisado: HUD y paneles mínimos
    // En el tutorial, la tarjeta guía ocupa la franja superior: reservamos más espacio
    // arriba para que las palabras (p. ej. CASA) queden DEBAJO y no se oculten tras ella.
    const tutorialTop = this.tutorialMode ? (compact || portrait ? 128 : 150) : 0;
    let insetTop, insetRight, insetLeft, insetBottom;
    if (portrait) {
      // El tablero ocupa DESDE justo bajo los jugadores HASTA justo encima de la mano.
      // Arriba SÍ se respeta la safe-area (el HUD superior baja con el notch), así que el
      // inset superior la incluye. Abajo la mano/acciones llegan al borde real (sin --sab),
      // por eso el inset inferior NO suma safe-area.
      insetTop = 122 + this._safeAreaTop() + tutorialTop; // pausa/reloj (~64) + jugadores (~56) + notch
      insetRight = 8;
      insetLeft = 8;
      insetBottom = 196;              // mano (~112) + barra de acciones pegada al fondo (~82)
    } else {
      insetTop = (compact ? 52 : 92) + tutorialTop;
      insetRight = compact ? 74 : Math.min(200, w * 0.28);  // barra de acciones (derecha)
      insetLeft = compact ? 142 : Math.min(210, w * 0.28);  // columna de jugadores (izq)
      insetBottom = compact ? 92 : this.HAND_PX + 8;
    }
    const usableW = Math.max(120, w - insetLeft - insetRight);
    const usableH = Math.max(120, h - insetTop - insetBottom);
    const cx = insetLeft + usableW / 2;
    const cy = insetTop + usableH / 2;
    return { usableW, usableH, offX: cx - w / 2, offY: cy - h / 2 };
  }

  /** Encaja unos límites de mundo en la ZONA ÚTIL (aleja lo necesario, sin pasar del origen). */
  fitTo(b) {
    const u = this._usableArea();
    const bw = Math.max(4, (b.maxX - b.minX) + 2.2);
    const bh = Math.max(3, (b.maxZ - b.minZ) + 2.2);
    const scale = Math.min(u.usableW / bw, u.usableH / bh);
    this.userZoom = clamp(scale / this.PPU, this.ZMIN, this.ZMAX);
    const s = this._scale();
    const ccx = (b.minX + b.maxX) / 2;
    const ccz = (b.minZ + b.maxZ) / 2;
    this.panX = ccx - u.offX / s;
    this.panZ = ccz - u.offY / s;
    this._applyCamera();
  }

  /** Zoom al MÁXIMO (origen) centrando el punto en la zona útil. */
  zoomToPoint(pt) {
    this.userZoom = 1;
    const u = this._usableArea();
    const s = this._scale();
    this.panX = pt.x - u.offX / s;
    this.panZ = pt.z - u.offY / s;
    this._applyCamera();
  }

  resetView() {
    this.userZoom = 1;
    this.panX = 0;
    this.panZ = 0;
    this._applyCamera();
  }

  _applyCamera() {
    const w = this.vw();
    const h = this.vh();
    const scale = this._scale();
    const viewW = w / scale;
    const viewH = h / scale;

    const cx = this.panX;
    const cz = this.panZ; // el posicionamiento (zona útil) se calcula en fitTo/zoomToPoint

    this.camera.left = -viewW / 2;
    this.camera.right = viewW / 2;
    this.camera.top = viewH / 2;
    this.camera.bottom = -viewH / 2;
    this.camera.position.set(cx, 50, cz);
    this.camera.lookAt(cx, 0, cz);
    this.camera.updateProjectionMatrix();

    // Marca de agua: centrada en pantalla, ~58% del alto útil, respetando aspecto.
    if (this.watermark) {
      const aspect = this._wmAspect || 1;
      let hW = viewH * 0.58;
      let wW = hW * aspect;
      const maxW = viewW * 0.82;
      if (wW > maxW) { wW = maxW; hW = wW / aspect; }
      this.watermark.scale.set(wW, hW, 1);
      this.watermark.position.set(cx, 0.05, cz);
    }

    // Reloj grande: cuadrado, centrado, ~62% del alto útil.
    if (this.bigTimer) {
      const size = Math.min(viewH * 0.62, viewW * 0.62);
      this._btBaseScale = size;
      this.bigTimer.scale.set(size, size, 1);
      this.bigTimer.position.set(cx, 0.06, cz);
    }
  }

  setQuality(q) {
    this.quality = q;
    this.shadowsEnabled = q !== 'low';
    this.renderer.shadowMap.enabled = this.shadowsEnabled;
    this.keyLight.castShadow = this.shadowsEnabled;
    this._applyPixelRatio();
  }

  dispose() {
    this.stop();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('orientationchange', this._onResize);
    this.renderer.dispose();
    this._updaters.clear();
  }
}
